import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  PropsWithChildren,
  useMemo,
} from 'react';
import {ApolloError, ApolloQueryResult, useLazyQuery, useQuery} from '@apollo/client';
import {useParams} from 'react-router-dom';
import {Box, Button} from '@mui/material';
import * as Sentry from '@sentry/react';
import {
  GET_ACCOUNT_BASIC,
  GET_ACCOUNT_FULL,
} from '../components/AccountOverview/accountOverviewQueries';
import ApiException from '../utils/exception';
import Loading from '../components/Loading/Loading';
import {useRecentClients} from '../hooks/useRecentClients';
import {Pot} from '../schema-types/Pot';

import {ShowIfDebugWrapper} from './DebugProvider';
import {BusinessUnitEnum} from '../schema-types/globalTypes';
import {AccountFull, AccountFullVariables} from '../schema-types/AccountFull';
import {LIST_POTS} from '../features/potQueries';
import {ListPots, ListPotsVariables} from '../schema-types/ListPots';
import {
  SingleAccountBasic,
  SingleAccountBasicVariables,
  SingleAccountBasic_accountDetails_validationContacts,
} from '../schema-types/SingleAccountBasic';
import {AccountBasicFragment} from '../schema-types/AccountBasicFragment';
import validateUUID from '../utils/validateUUID';

export type AccountContextType = {
  account: AccountBasicFragment | undefined;
  validationContacts: SingleAccountBasic_accountDetails_validationContacts[];
  refetchAccount: () => Promise<ApolloQueryResult<SingleAccountBasic>>;
  accountLoading: boolean;
  accountError: ApolloError | undefined;
  isFullGoalSteps: boolean;
  setIsFullGoalSteps: React.Dispatch<React.SetStateAction<boolean>>;
  pots: Pot[];
  potsLoading: boolean;
  accountId?: string;
  accountType: BusinessUnitEnum | null;
};

export type AccountNavigationParams = {
  accountPageTabKey: string;
  accountId: string;
  reviewId: string;
};

export const AccountContext = createContext<AccountContextType>({} as AccountContextType);

export const useAccountContext = () => useContext(AccountContext);

export const useAccountFullDelayed = (accountId: string) => {
  const [
    fetchFull,
    {loading: accountLoading, error: accountError, data: account, refetch},
  ] = useLazyQuery<AccountFull, AccountFullVariables>(GET_ACCOUNT_FULL, {
    variables: {
      accountId: validateUUID(accountId)!,
    },
  });

  return {
    accountFull: account?.accountDetails,
    fetchFull,
  };
};

export const useAccountFull = () => {
  const {accountId} = useAccountContext();
  const {
    loading: accountLoading,
    error: accountError,
    data: account,
    refetch,
  } = useQuery<AccountFull, AccountFullVariables>(GET_ACCOUNT_FULL, {
    variables: {
      accountId: validateUUID(accountId)!,
    },
    skip: !accountId,
  });

  return {
    accountFull: account?.accountDetails,
  };
};

export const AccountProvider: React.FC<
  PropsWithChildren<{makeEmptyIfNoAccount?: boolean}>
> = ({makeEmptyIfNoAccount, children}) => {
  const {accountId} = useParams<AccountNavigationParams>();
  const {setRecentSearch} = useRecentClients();

  const [isFullGoalSteps, setIsFullGoalSteps] = useState(true);
  // const runningE2ELocally =
  //   getEnvironment() === 'DEV' && window.localStorage.getItem(`e2e-$`) === 'true';
  const {fetchFull} = useAccountFullDelayed(accountId!);
  const {
    data: accountPotsData,
    loading: accountPotsLoading,
    refetch: refetchPots,
  } = useQuery<ListPots, ListPotsVariables>(LIST_POTS, {
    variables: {
      accountId: validateUUID(accountId)!,
      parentOnly: true,
      curoSync: true,
    },
  });

  useEffect(() => {
    setTimeout(() => fetchFull(), 5000);
  }, [fetchFull]);

  const {
    loading: accountLoading,
    error: accountError,
    data: accountData,
    refetch: refetchAccount,
  } = useQuery<SingleAccountBasic, SingleAccountBasicVariables>(GET_ACCOUNT_BASIC, {
    variables: {
      accountId: validateUUID(accountId)!,
      curoSync: process.env.NODE_ENV !== 'development',
    },
    skip: !accountId,
  });

  useEffect(() => {
    if (accountData?.accountDetails) {
      setRecentSearch(accountData.accountDetails);
      try {
        Sentry.setTag('accountId', accountId || '');
        Sentry.setTag('accountCuroId', accountData.accountDetails.curoId || '');
        Sentry.setTag('accountName', accountData.accountDetails.name || '');
        Sentry.setTag(
          'accountType',
          accountData.accountDetails.businessUnit?.partnershipType || ''
        );
      } catch (err) {
        // do nothing
      }
    }
  }, [accountData, accountId]);

  const value: AccountContextType = useMemo(() => {
    const accountType = accountData?.accountDetails.businessUnit?.partnershipType || null;
    return {
      accountId,
      account: accountData?.accountDetails,
      validationContacts: accountData?.accountDetails?.validationContacts ?? [],
      accountLoading,
      accountError,
      isFullGoalSteps,
      setIsFullGoalSteps,
      pots: accountPotsData?.pots || [],
      refetchAccount: async () => {
        return refetchAccount({
          accountId: validateUUID(accountId)!,
        });
      },
      potsLoading: accountPotsLoading,
      accountType,
    };
  }, [
    accountId,
    accountData?.accountDetails,
    accountLoading,
    accountError,
    isFullGoalSteps,
    setIsFullGoalSteps,
    accountPotsData?.pots,
    refetchAccount,
    accountPotsLoading,
  ]);

  if (accountLoading) {
    if (makeEmptyIfNoAccount) {
      return null;
    }
    return <Loading entityName="account" />;
  }

  if (accountError || accountData?.accountDetails === null) {
    if (accountError?.graphQLErrors.some((x) => x.message.includes('does not match'))) {
      throw new ApiException('Your business unit does not allow viewing this account');
    }

    throw new ApiException(accountError?.message);
  }

  return (
    <>
      <ShowIfDebugWrapper>
        <Box position="fixed" zIndex={100} top={37} left={135}>
          <Button
            variant="text"
            color="secondary"
            size="small"
            onClick={() =>
              navigator.clipboard.writeText(accountData?.accountDetails?.curoId)
            }
          >
            Account curo id: {accountData?.accountDetails?.curoId}
          </Button>
        </Box>
      </ShowIfDebugWrapper>
      <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
    </>
  );
};

export default AccountProvider;
